import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { closeCreateInvoiceModal, createInvoiceValidationSchema, selectIsCreateInvoiceModalOpen, selectOnCreate } from "../model"
import { SubmitHandler, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { Modal } from "@/shared/ui/modal"
import { FormInput } from "@/shared/ui/form-input"
import { createInvoice, getInvoicePdf, InvoiceType, InvoiceTypes } from "@/shared/api/invoices"
import notify from "@/shared/lib/notify"
import { getErrorMessage } from "@/shared/consts/error-codes"
import { useEffect, useState } from "react"
import InfoSvg from "@/shared/assets/images/Info.svg"
import { animated, useTransition } from "@react-spring/web"

interface CreateInvoiceInitialState {
    amount: number
    type: InvoiceType
    ofertaAccepted: boolean
}

const initialState: CreateInvoiceInitialState = {
    amount: 0,
    type: "discounts",
    ofertaAccepted: false
}

const invoiceTypeSelectOptions = [
    {
        value: "discounts",
        label: "Организация скидок"
    },
    {
        value: "fuel",
        label: "Купить СУГ"
    },
    {
        value: "aviabooking",
        label: "Купить Авиабилет"
    },
    {
        value: "food_service",
        label: "Организация питания"
    }
]

const InvoiceTypeDetails: Record<InvoiceType, { oferta: string, description: React.ReactNode, ofertaLink: string }> = {
    [InvoiceTypes.fuel]: {
        description: (
            <div>
                Вы получите бонус <span className="text-color-primary">FUEL</span>, который сможете  <br />использовать для заправки.
            </div>
        ),
        oferta: "Офета FUEL",
        ofertaLink: "/doc/legal-inv-fuel.pdf",
    },
    [InvoiceTypes.aviabooking]: {
        description: (
            <div>
                Вы получите бонус <span className="text-color-primary">FLY</span>, который сможете  <br />использовать для покупки авиабилетов.
            </div>
        ),
        oferta: "Офета AVIABOOKING",
        ofertaLink: "/doc/legal-inv-fly.pdf",
    },
    [InvoiceTypes.discounts]: {
        description: (
            <div>
                Вы получите бонус <span className="text-color-primary">BON</span>, который сможете  <br />использовать для организации скидок.
            </div>
        ),
        oferta: "Офета Скидки",
        ofertaLink: "/doc/legal-inv-bon.pdf",
    },
    [InvoiceTypes.foodService]: {
        description: (
            <div>
                Вы получите бонус <span className="text-color-primary">BON</span>, который сможете  <br />использовать для организации питания.
            </div>
        ),
        oferta: "Офета питание",
        ofertaLink: "/doc/legal-inv-food-service.pdf",
    }
}

export const CreateInvoiceModal = () => {
    const dispatch = useAppDispatch()
    const isOpen = useAppSelector(selectIsCreateInvoiceModalOpen)
    const onCreate = useAppSelector(selectOnCreate)
    const [stage, setStage] = useState<number>(0)
    const handleClose = () => {
        dispatch(closeCreateInvoiceModal())
        setStage(0)
    }
    const transitions = useTransition(stage, {
        from: { transform: 'translateX(100%)', opacity: 0 },
        enter: { transform: 'translateX(0%)', position: 'relative', opacity: 1 },
        leave: { transform: 'translateX(-100%)', position: 'absolute', opacity: 0 },
        config: { tension: 120, friction: 20 }
    });

    const handlePrev = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault()
        setStage(0)
    }

    const handleNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault()
        setStage(1)
    }

    const onSubmit: SubmitHandler<typeof initialState> = async (data) => {
        await new Promise(async (resolve, reject) => {
            try {
                await createInvoice(data).then(async (res) => {
                    await getInvoicePdf(res.data.id).then((resp) => {
                        notify.success('Счет успешно создан')
                        const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                        const link = document.createElement('a');
                        link.href = url;

                        link.setAttribute(
                            'target',
                            `_blank`,
                        );
                        document.body.appendChild(link);
                        link.click();
                        link.parentNode.removeChild(link);
                        resolve('')
                        reset()
                        onCreate?.()
                        handleClose()
                    })
                })
            } catch (e) {
                notify.error(getErrorMessage(e.response?.data?.error_id))
                reject()
            }
        })
    }

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, touchedFields },
        reset,
        control,
        watch
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(createInvoiceValidationSchema)
    })

    useEffect(() => {
        console.log(touchedFields)
    }, [touchedFields])

    return (
        <Modal
            isOpen={isOpen}
            title="Пополнение баланса"
            onRequestClose={handleClose}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="w-full mb-4 overflow-hidden relative">
                    <div className={`w-full flex relative`}>
                        {transitions((style, item) => 
                            item === 0
                            ? <animated.div style={style} key={item} className={`w-full`}>
                                <div className="w-full flex flex-col gap-3">
                                    <FormInput
                                        register={register}
                                        label={"Тип пополнения"}
                                        id={"type"}
                                        type="select"
                                        error={errors['type']}
                                        control={control}
                                        options={invoiceTypeSelectOptions}
                                        required={true}
                                        placeholder="Выберите тип пополнения"
                                    />
                                    <div className="flex items-center gap-2">
                                        <InfoSvg /> 
                                        {InvoiceTypeDetails[watch('type')].description}
                                    </div>
                                </div>
                            </animated.div>
                            : <animated.div style={style} key={item} className={`w-full`}>
                                <div className="w-full flex flex-col gap-3">
                                    <FormInput
                                        register={register}
                                        label={"Количество бонусов"}
                                        id={"amount"}
                                        type="number"
                                        error={Boolean(touchedFields['amount']) && errors['amount']}
                                        required={true}
                                        placeholder="Введите количество бонусов"
                                    />
                                    <div className="flex flex-col">
                                        {errors['ofertaAccepted'] && touchedFields['ofertaAccepted'] &&
                                            <div className="text-color-danger mb-1">
                                                {errors['ofertaAccepted'].message}
                                            </div>
                                        }
                                        <div className="flex gap-2 items-center">
                                            <input
                                                className="relative w-[3.25rem] h-7 bg-[#B9C9C6] checked:bg-none checked:bg-color-primary border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 ring-0 ring-transparent focus:ring-transparent ring-offset-white focus:outline-0 appearance-none dark:bg-black/20 dark:checked:bg-primary dark:focus:ring-offset-white/10 before:inline-block before:w-6 before:h-6 before:bg-white checked:before:bg-white before:translate-x-0 checked:before:translate-x-full before:shadow before:rounded-full before:transform before:ring-0 before:transition before:ease-in-out before:duration-200 "
                                                type="checkbox"
                                                {...register("ofertaAccepted", { required: true })}
                                            />
                                            <div>
                                                Согласен с условиями <a target="_blank" href={InvoiceTypeDetails[watch('type')].ofertaLink} className="text-color-primary">оферты</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </animated.div>
                        )}
                    </div>  
                </div>
                <div className="w-full flex gap-3">
                    {stage === 0
                        ? <button disabled={isSubmitting} onClick={handleClose} className="form_cancel_button">
                            Отменить
                        </button>
                        : <button onClick={handlePrev} className="form_cancel_button">
                            Назад
                        </button>
                    }
                    {stage === 1
                        ? <button disabled={isSubmitting} className="form_handle_button" type="submit">
                            Сформировать счет
                        </button>
                        : <button onClick={handleNext} className="form_handle_button">
                            Далее
                        </button>
                    }
                    
                </div>
            </form>
        </Modal>
    )
}